import { useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    getAllSipStokHareketleri,
    getUrIsEmri,
    getUrAkisOperasyonMamul,
    getTumIsEmriPlan,
    getAltIsEmri,
} from '../api/aaroService';

// Belirtilen süre kadar beklemek için delay fonksiyonu
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * İş emrini ve alt iş emirlerini sıralı olarak çeken rekürsif fonksiyon
 */
const fetchIsEmriRecursive = async (isEmriID, logger, fetchedIDs = new Set(), depth = 0, abortSignal) => {
    // Dairesel referansları ve aşırı derin özyinelemeli çağrıları önle
    if (fetchedIDs.has(isEmriID) || depth > 10 || abortSignal?.aborted) {
        if (depth > 10) {
            logger?.warning(`Maksimum derinliğe ulaşıldı. İşEmriID: ${isEmriID}`);
        }
        return null;
    }

    fetchedIDs.add(isEmriID);

    let isEmri = null;
    let operasyonlar = [];
    let altIsEmirleri = [];
    let uretimPlanlari = [];

    try {
        // 1) Ana İş Emri
        logger?.info(`İş Emri çekiliyor: ${isEmriID}`);
        const respIsEmri = await getUrIsEmri(isEmriID, {
            signal: abortSignal,
            retryCount: 3,
            retryDelay: 600,
        });

        if (!respIsEmri?.Model) {
            logger?.error(`İş Emri ${isEmriID} bulunamadı!`);
            return null;
        }

        isEmri = respIsEmri.Model;
        logger?.success(`İş Emri ${isEmriID} alındı.`);

        if (abortSignal?.aborted) return null;

        // 2) Operasyonlar
        const respOperasyonlar = await getUrAkisOperasyonMamul(isEmriID, {
            signal: abortSignal,
            minDelay: 400,
        });

        if (respOperasyonlar?.Model && Array.isArray(respOperasyonlar.Model)) {
            operasyonlar = respOperasyonlar.Model;
            logger?.success(`${isEmriID} için ${operasyonlar.length} operasyon alındı.`);
        }

        if (abortSignal?.aborted) return null;

        // 3) Üretim Planları
        const respOp = await getTumIsEmriPlan(isEmriID, {
            signal: abortSignal,
            minDelay: 400,
        });

        if (respOp?.Model && Array.isArray(respOp.Model)) {
            uretimPlanlari = respOp.Model;
        }

        if (abortSignal?.aborted) return null;

        // 4) Alt İş Emirleri (rekürsif)
        const respAlt = await getAltIsEmri(isEmriID, {
            signal: abortSignal,
            minDelay: 400,
        });

        if (respAlt?.Model && Array.isArray(respAlt.Model)) {
            // Alt iş emirlerini paralel değil sıralı olarak çekelim
            for (const alt of respAlt.Model) {
                if (abortSignal?.aborted) return null;

                if (alt.IsEmriID && alt.IsEmriID !== isEmriID) {
                    // İlave kontrol: kendisine referans olmamalı
                    // Her alt iş emri çağrısı öncesinde ilave gecikme
                    await delay(500);

                    const child = await fetchIsEmriRecursive(alt.IsEmriID, logger, fetchedIDs, depth + 1, abortSignal);

                    if (child) {
                        altIsEmirleri.push(child);
                    }
                }
            }
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return null;
        }

        // Hata detaylarını logla
        const errorDetails = err.status ? `Hata kodu: ${err.status}, Mesaj: ${err.message}` : err.message;

        logger?.error(`İş Emri ${isEmriID} işlenirken hata: ${errorDetails}`);
        return null;
    }

    return { isEmri, operasyonlar, uretimPlanlari, altIsEmirleri };
};

/**
 * Sipariş verileri için React Query hook'u
 * @param {Object} params - Parametreler
 * @param {string} params.dekontID - Dekont ID
 * @param {Object} params.logger - Loglama işlemleri için logger nesnesi
 * @returns {Object} React Query sonuçları
 */
const useIsEmriQuery = ({ dekontID, logger }) => {
    const abortControllerRef = useRef(null);

    // İşlemleri iptal etme fonksiyonu
    const cancelFetch = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            logger?.warning('Veri çekme işlemi iptal edildi');
        }
    }, [logger]);

    // Stok hareketleri sorgusu
    const stokHareketleriQuery = useQuery(
        ['SipStokHareketleri', dekontID],
        async ({ signal }) => {
            if (!dekontID) return [];

            logger?.info(`DekontID: ${dekontID} => stok hareketleri çekiliyor...`);
            abortControllerRef.current = new AbortController();

            const data = await getAllSipStokHareketleri(dekontID, {
                onProgress: (msg) => logger?.info(msg),
                signal: abortControllerRef.current.signal || signal,
                delayMs: 300,
                maxPages: 100,
            });

            logger?.success(`Toplam ${data.length} adet stok hareketi bulundu.`);
            return data;
        },
        {
            enabled: !!dekontID,
            staleTime: 1000 * 60 * 5, // 5 dakika
            refetchOnWindowFocus: false,
            onError: (error) => {
                logger?.error(`Stok hareketleri çekilirken hata: ${error.message}`);
            },
        }
    );

    // Stok hareketlerinden gelen verileri kullanarak iş emirlerini çeken sorgu
    const isEmriQuery = useQuery(
        ['isEmriData', stokHareketleriQuery.data?.map((sh) => sh.HareketID).join(',')],
        async ({ signal }) => {
            const stokHareketleri = stokHareketleriQuery.data || [];

            if (!stokHareketleri.length) return [];

            logger?.info(`İş emirlerini çekmeye başlıyorum...`);

            // Sadece benzersiz iş emri ID'lerini işle
            const uniqueIsEmriIDs = [
                ...new Set(stokHareketleri.filter((hareket) => hareket?.IsEmriID).map((hareket) => hareket.IsEmriID)),
            ];

            logger?.info(`İşlenecek toplam ${uniqueIsEmriIDs.length} benzersiz iş emri bulundu.`);

            const results = [];
            const fetchedIDs = new Set();
            let islemSayisi = 0;

            abortControllerRef.current = new AbortController();

            // Her benzersiz İş Emri için işleme yap
            for (const isEmriID of uniqueIsEmriIDs) {
                if (signal?.aborted || abortControllerRef.current.signal.aborted) break;

                islemSayisi++;
                logger?.info(`İşEmriID: ${isEmriID} alınıyor... (${islemSayisi}/${uniqueIsEmriIDs.length})`);

                try {
                    await delay(300); // API'ye nefes aldıralım

                    const emriObj = await fetchIsEmriRecursive(
                        isEmriID,
                        logger,
                        fetchedIDs,
                        0,
                        abortControllerRef.current.signal
                    );

                    if (emriObj) {
                        // Bu iş emriyle ilişkili tüm stok hareketlerini bul
                        const relatedHareketler = stokHareketleri.filter((h) => h.IsEmriID === isEmriID);

                        for (const hareket of relatedHareketler) {
                            results.push({ stokHareketi: hareket, isEmri: emriObj });
                        }

                        logger?.success(`İşEmriID: ${isEmriID} başarıyla işlendi. (${results.length} sonuç hazır)`);
                    } else {
                        logger?.error(`İşEmriID: ${isEmriID} için veri alınamadı veya boş döndü.`);
                    }
                } catch (err) {
                    if (err.name === 'AbortError') break;
                    logger?.error(`İşEmriID: ${isEmriID} işlenirken hata: ${err.message}`);
                }
            }
            console.log(results);
            logger?.success(`İşEmri verileri alındı. Toplam ${results.length} kayıt.`);
            return results;
        },
        {
            enabled: stokHareketleriQuery.isSuccess && !!stokHareketleriQuery.data?.length,
            staleTime: 1000 * 60 * 5, // 5 dakika
            refetchOnWindowFocus: false,
            onError: (error) => {
                logger?.error(`İş emirleri çekilirken hata: ${error.message}`);
            },
        }
    );

    return {
        stokHareketleri: stokHareketleriQuery.data || [],
        isEmriData: isEmriQuery.data || [],
        isLoading: stokHareketleriQuery.isLoading || isEmriQuery.isLoading,
        isFetching: stokHareketleriQuery.isFetching || isEmriQuery.isFetching,
        isRefetching: stokHareketleriQuery.isRefetching || isEmriQuery.isRefetching,
        error: stokHareketleriQuery.error || isEmriQuery.error,
        refetch: stokHareketleriQuery.refetch,
        cancelFetch,
    };
};

export default useIsEmriQuery;
